import React, { useCallback, useState, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../lib/tenant-context';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Upload, FileText, CheckCircle2, AlertCircle, ChevronRight, ArrowLeft, Rocket, LayoutDashboard, UtensilsCrossed, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { parseCsv, validateCsv, structureMenu, importMenu } from '../../lib/csv-import';
import type { ValidationResult, StructuredMenu, ImportResult } from '../../lib/csv-import';
type Step = 'upload' | 'validate' | 'preview' | 'confirm' | 'success';
const STEPS: {
  key: Step;
  label: string;
}[] = [{
  key: 'upload',
  label: 'Subir'
}, {
  key: 'validate',
  label: 'Validar'
}, {
  key: 'preview',
  label: 'Vista previa'
}, {
  key: 'confirm',
  label: 'Confirmar'
}, {
  key: 'success',
  label: 'Listo'
}];
export function ImportPage() {
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [menu, setMenu] = useState<StructuredMenu | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // ─── File handling ─────────────────────────────────────────
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      alert('Solo se aceptan archivos .csv');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      setRawText(text);
      // Auto-advance to validation
      const rawRows = parseCsv(text);
      const validationResult = validateCsv(rawRows);
      setValidation(validationResult);
      setStep('validate');
    };
    reader.readAsText(file);
  }, []);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };
  // ─── Step transitions ──────────────────────────────────────
  const goToPreview = () => {
    if (!validation) return;
    const structured = structureMenu(validation.validRows);
    setMenu(structured);
    setStep('preview');
  };
  const goToConfirm = () => setStep('confirm');
  const handleImport = async () => {
    if (!menu || !restaurant) return;
    setImporting(true);
    const importResult = await importMenu(restaurant.id, menu);
    setResult(importResult);
    setImporting(false);
    if (importResult.success) {
      setStep('success');
    }
  };
  const reset = () => {
    setStep('upload');
    setFileName(null);
    setRawText(null);
    setValidation(null);
    setMenu(null);
    setResult(null);
  };
  // ─── Step index for progress ───────────────────────────────
  const currentStepIdx = STEPS.findIndex(s => s.key === step);
  // ─── Render ────────────────────────────────────────────────
  return <AdminLayout restaurant={restaurant} role={role} onSignOut={signOut} data-id="element-2668">
      <div className="max-w-3xl mx-auto px-4 py-8" data-id="element-2669">
        {/* Header */}
        <div className="mb-8" data-id="element-2670">
          <button onClick={() => navigate(`/admin/${restaurant.slug}/menu`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4" data-id="element-2671">
            <ArrowLeft className="w-4 h-4" data-id="element-2672" />
            Volver al menú
          </button>
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2673">
            Importar menú desde CSV
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2674">
            Sube un archivo CSV con tu menú y lo crearemos automáticamente.
          </p>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1 mb-10" data-id="element-2675">
          {STEPS.map((s, idx) => <Fragment key={s.key} data-id="element-2676">
              <div className="flex items-center gap-2" data-id="element-2677">
                <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors', idx < currentStepIdx ? 'bg-green-500 text-white' : idx === currentStepIdx ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400')} data-id="element-2678">
                  {idx < currentStepIdx ? <CheckCircle2 className="w-4 h-4" data-id="element-2679" /> : idx + 1}
                </div>
                <span className={cn('text-xs font-medium hidden sm:block', idx <= currentStepIdx ? 'text-gray-900' : 'text-gray-400')} data-id="element-2680">
                  {s.label}
                </span>
              </div>
              {idx < STEPS.length - 1 && <div className={cn('flex-1 h-0.5 rounded-full mx-1', idx < currentStepIdx ? 'bg-green-500' : 'bg-gray-100')} data-id="element-2681" />}
            </Fragment>)}
        </div>

        {/* ═══ STEP 1: UPLOAD ═══ */}
        {step === 'upload' && <div onDragOver={e => {
        e.preventDefault();
        setDragOver(true);
      }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={cn('border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all', dragOver ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50')} data-id="element-2682">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" data-id="element-2683" />
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-2684">
              <Upload className="w-6 h-6 text-gray-400" data-id="element-2685" />
            </div>
            <p className="text-base font-semibold text-gray-900 mb-1" data-id="element-2686">
              Arrastra tu archivo CSV aquí
            </p>
            <p className="text-sm text-gray-500 mb-6" data-id="element-2687">
              o haz clic para seleccionar
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg" data-id="element-2688">
              <FileText className="w-4 h-4" data-id="element-2689" />
              Seleccionar archivo
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-2690">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2691">
                Columnas requeridas
              </p>
              <div className="flex flex-wrap justify-center gap-2" data-id="element-2692">
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-2693">
                  category_name
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-2694">
                  item_name
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-2695">
                  price
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-2696">
                  station_code
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-2" data-id="element-2697">
                Opcionales: description, option_name, option_price
              </p>
            </div>
          </div>}

        {/* ═══ STEP 2: VALIDATE ═══ */}
        {step === 'validate' && validation && <div className="space-y-6" data-id="element-2698">
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4" data-id="element-2699">
              <div className="bg-green-50 border border-green-200 rounded-xl p-5" data-id="element-2700">
                <div className="flex items-center gap-2 mb-1" data-id="element-2701">
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-2702" />
                  <span className="text-sm font-semibold text-green-800" data-id="element-2703">
                    Filas válidas
                  </span>
                </div>
                <p className="text-3xl font-black text-green-700" data-id="element-2704">
                  {validation.validRows.length}
                </p>
              </div>
              <div className={cn('border rounded-xl p-5', validation.invalidRows.length > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200')} data-id="element-2705">
                <div className="flex items-center gap-2 mb-1" data-id="element-2706">
                  <AlertCircle className={cn('w-5 h-5', validation.invalidRows.length > 0 ? 'text-red-500' : 'text-gray-400')} data-id="element-2707" />
                  <span className={cn('text-sm font-semibold', validation.invalidRows.length > 0 ? 'text-red-800' : 'text-gray-600')} data-id="element-2708">
                    Filas con errores
                  </span>
                </div>
                <p className={cn('text-3xl font-black', validation.invalidRows.length > 0 ? 'text-red-600' : 'text-gray-400')} data-id="element-2709">
                  {validation.invalidRows.length}
                </p>
              </div>
            </div>

            {/* Invalid rows detail */}
            {validation.invalidRows.length > 0 && <div className="bg-white border border-red-200 rounded-xl overflow-hidden" data-id="element-2710">
                <div className="px-5 py-3 bg-red-50 border-b border-red-200" data-id="element-2711">
                  <p className="text-sm font-semibold text-red-800" data-id="element-2712">
                    Errores encontrados
                  </p>
                </div>
                <div className="max-h-60 overflow-y-auto divide-y divide-red-100" data-id="element-2713">
                  {validation.invalidRows.map(row => <div key={row.row} className="px-5 py-3" data-id="element-2714">
                      <div className="flex items-center gap-2 mb-1" data-id="element-2715">
                        <span className="text-xs font-bold text-red-500 bg-red-100 px-2 py-0.5 rounded" data-id="element-2716">
                          Fila {row.row}
                        </span>
                      </div>
                      <ul className="space-y-0.5" data-id="element-2717">
                        {row.errors.map((err, i) => <li key={i} className="text-xs text-red-700 flex items-center gap-1" data-id="element-2718">
                            <span data-id="element-2719">•</span> {err}
                          </li>)}
                      </ul>
                    </div>)}
                </div>
              </div>}

            {/* File info */}
            {fileName && <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-200" data-id="element-2720">
                <FileText className="w-5 h-5 text-gray-400" data-id="element-2721" />
                <span className="text-sm text-gray-700 font-medium flex-1 truncate" data-id="element-2722">
                  {fileName}
                </span>
                <button onClick={reset} className="text-xs text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2723">
                  Cambiar archivo
                </button>
              </div>}

            {/* Actions */}
            <div className="flex gap-3" data-id="element-2724">
              <button onClick={reset} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2725">
                Volver
              </button>
              <button onClick={goToPreview} disabled={validation.validRows.length === 0} className={cn('flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold rounded-xl transition-colors', validation.validRows.length > 0 ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-2726">
                Continuar con {validation.validRows.length} filas
                <ChevronRight className="w-4 h-4" data-id="element-2727" />
              </button>
            </div>
          </div>}

        {/* ═══ STEP 3: PREVIEW ═══ */}
        {step === 'preview' && menu && <div className="space-y-6" data-id="element-2728">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3" data-id="element-2729">
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-2730">
                <p className="text-2xl font-black text-gray-900" data-id="element-2731">
                  {menu.totalCategories}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-2732">
                  Categorías
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-2733">
                <p className="text-2xl font-black text-gray-900" data-id="element-2734">
                  {menu.totalItems}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-2735">
                  Productos
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-2736">
                <p className="text-2xl font-black text-gray-900" data-id="element-2737">
                  {menu.totalOptions}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-2738">
                  Opciones
                </p>
              </div>
            </div>

            {/* Category breakdown */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-2739">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200" data-id="element-2740">
                <p className="text-sm font-semibold text-gray-900" data-id="element-2741">
                  Vista previa del menú
                </p>
              </div>
              <div className="divide-y divide-gray-100 max-h-[28rem] overflow-y-auto" data-id="element-2742">
                {menu.categories.map(cat => <div key={cat.name} className="px-5 py-4" data-id="element-2743">
                    <div className="flex items-center justify-between mb-3" data-id="element-2744">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-2745">
                        {cat.name}
                      </h3>
                      <span className="text-xs text-gray-400 font-medium" data-id="element-2746">
                        {cat.items.length}{' '}
                        {cat.items.length === 1 ? 'producto' : 'productos'}
                      </span>
                    </div>
                    <div className="space-y-2" data-id="element-2747">
                      {cat.items.map(item => <div key={`${cat.name}-${item.name}`} className="flex items-start justify-between gap-4 py-1.5" data-id="element-2748">
                          <div className="flex-1 min-w-0" data-id="element-2749">
                            <p className="text-sm text-gray-800 font-medium truncate" data-id="element-2750">
                              {item.name}
                            </p>
                            {item.description && <p className="text-xs text-gray-400 truncate" data-id="element-2751">
                                {item.description}
                              </p>}
                            {item.options.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-2752">
                                {item.options.map(opt => <span key={opt.name} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium" data-id="element-2753">
                                    {opt.name}
                                    {opt.price > 0 && ` +₡${opt.price}`}
                                  </span>)}
                              </div>}
                          </div>
                          <div className="text-right shrink-0" data-id="element-2754">
                            <p className="text-sm font-bold text-gray-900" data-id="element-2755">
                              ₡{item.price.toLocaleString()}
                            </p>
                            <p className="text-[10px] text-gray-400 font-mono" data-id="element-2756">
                              {item.station_code}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3" data-id="element-2757">
              <button onClick={() => setStep('validate')} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2758">
                Volver
              </button>
              <button onClick={goToConfirm} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors" data-id="element-2759">
                Todo se ve bien
                <ChevronRight className="w-4 h-4" data-id="element-2760" />
              </button>
            </div>
          </div>}

        {/* ═══ STEP 4: CONFIRM ═══ */}
        {step === 'confirm' && menu && <div className="space-y-6" data-id="element-2761">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center" data-id="element-2762">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-2763">
                <span className="text-3xl" data-id="element-2764">🚀</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2765">
                ¿Crear el menú?
              </h2>
              <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6" data-id="element-2766">
                Se crearán {menu.totalCategories} categorías, {menu.totalItems}{' '}
                productos
                {menu.totalOptions > 0 && ` y ${menu.totalOptions} opciones`} en{' '}
                <strong data-id="element-2767">{restaurant.name}</strong>.
              </p>

              {result && !result.success && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-left" data-id="element-2768">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2769">
                    <AlertCircle className="w-4 h-4 text-red-500" data-id="element-2770" />
                    <span className="text-sm font-semibold text-red-800" data-id="element-2771">
                      Error durante la importación
                    </span>
                  </div>
                  <p className="text-xs text-red-700" data-id="element-2772">{result.error}</p>
                  <p className="text-xs text-red-500 mt-1" data-id="element-2773">
                    Se crearon {result.categoriesCreated} categorías,{' '}
                    {result.itemsCreated} productos y {result.optionsCreated}{' '}
                    opciones antes del error.
                  </p>
                </div>}

              <div className="flex gap-3" data-id="element-2774">
                <button onClick={() => setStep('preview')} disabled={importing} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2775">
                  Volver
                </button>
                <button onClick={handleImport} disabled={importing} className={cn('flex-1 flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-bold rounded-xl transition-colors', importing ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-green-600 text-white hover:bg-green-700')} data-id="element-2776">
                  {importing ? <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-2777">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2778" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-2779" />
                      </svg>
                      Creando menú...
                    </> : <>
                      <Rocket className="w-4 h-4" data-id="element-2780" />
                      Crear menú
                    </>}
                </button>
              </div>
            </div>
          </div>}

        {/* ═══ STEP 5: SUCCESS ═══ */}
        {step === 'success' && result && <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center" data-id="element-2781">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2782">
              <span className="text-4xl" data-id="element-2783">🚀</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2784">
              ¡Tu menú está listo!
            </h2>
            <p className="text-sm text-gray-500 max-w-sm mx-auto mb-2" data-id="element-2785">
              Se crearon exitosamente:
            </p>
            <div className="flex justify-center gap-6 mb-8" data-id="element-2786">
              <div className="text-center" data-id="element-2787">
                <p className="text-2xl font-black text-green-600" data-id="element-2788">
                  {result.categoriesCreated}
                </p>
                <p className="text-xs text-gray-500" data-id="element-2789">categorías</p>
              </div>
              <div className="text-center" data-id="element-2790">
                <p className="text-2xl font-black text-green-600" data-id="element-2791">
                  {result.itemsCreated}
                </p>
                <p className="text-xs text-gray-500" data-id="element-2792">productos</p>
              </div>
              {result.optionsCreated > 0 && <div className="text-center" data-id="element-2793">
                  <p className="text-2xl font-black text-green-600" data-id="element-2794">
                    {result.optionsCreated}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2795">opciones</p>
                </div>}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto" data-id="element-2796">
              <button onClick={() => navigate(`/admin/${restaurant.slug}/menu`)} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors" data-id="element-2797">
                <UtensilsCrossed className="w-4 h-4" data-id="element-2798" />
                Ver menú
              </button>
              <button onClick={() => navigate(`/admin/${restaurant.slug}/dashboard`)} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2799">
                <LayoutDashboard className="w-4 h-4" data-id="element-2800" />
                Dashboard
              </button>
            </div>
          </div>}
      </div>
    </AdminLayout>;
}